import React, { useState } from "react";
import { IconButton, List, Menu } from "@sscs/ui";
import {
  ApprovedIcon,
  AssignmentIcon as MUIAssignment,
  DeleteIcon as MUIDelete,
  CloudDownloadIcon as MUIDownload,
  MoveFileIcon as MUIDriveFileMove,
  EditIcon as MUIEdit,
  LockResetIcon as MUILockReset,
  MoreVertIcon as MUIMoreVert,
  RestartAltIcon as MUIRestartAlt,
  RestoreIcon as MUISettingsBackupRestore,
  UndoIcon as MUIUndo,
  RevokedIcon,
} from "@sscs/ui/icons";
import { Permission } from "Constants";
import { ActionItem, ActionType } from "Types";
import { AllowedTo } from "../";
import styles from "./actionMenu.module.scss";

const typeToIcon: Record<Exclude<ActionType, "CUSTOM">, React.JSX.Element> = {
  EDIT: <MUIEdit />,
  DELETE: <MUIDelete />,
  REANALYZE: <MUIRestartAlt />,
  VIEW_REPORT: <MUIAssignment />,
  RESET: <MUILockReset />,
  UNDO: <MUIUndo />,
  REVERT: <MUISettingsBackupRestore />,
  REVOKE_APPROVAL: <RevokedIcon />,
  APPROVAL: <ApprovedIcon />,
  DOWNLOAD: <MUIDownload />,
  MOVE_TO_PROJECT: <MUIDriveFileMove />,
};

// Map the IconButton size onto the icon size scale.
const BUTTON_ICON_SIZE: Record<string, "sm" | "md" | "lg"> = {
  small: "sm",
  medium: "md",
  large: "lg",
};

type ActionMenuProps = {
  id?: string;
  items: ActionItem[];
  size?: React.ComponentProps<typeof IconButton>["size"];
  disabled?: boolean;
};

function ActionMenu({
  id,
  items,
  size = "medium",
  disabled,
}: ActionMenuProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuItemClick = (onClick: () => void) => {
    onClick();
    setAnchorEl(null);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        data-testid="action-menu"
        id={id}
        onClick={handleOpenMenuClick}
        size={size}
        disabled={disabled}
      >
        <MUIMoreVert
          size={BUTTON_ICON_SIZE[size ?? "medium"]}
          className={styles["menu-icon"]}
        />
      </IconButton>
      <Menu.Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        {items.map((action: ActionItem) => (
          <ConditionalPermissionsWrapper
            permissions={action?.permissions}
            key={action?.id ?? action.type}
          >
            <Menu.Item
              id={action?.id}
              disabled={action.isDisabled}
              onClick={() => handleMenuItemClick(action.onClick)}
            >
              <List.ItemIcon className={styles["menu-icon"]}>
                {action.type !== "CUSTOM"
                  ? typeToIcon[action.type]
                  : action.itemIcon}
              </List.ItemIcon>
              <List.ItemText>{action.text}</List.ItemText>
            </Menu.Item>
          </ConditionalPermissionsWrapper>
        ))}
      </Menu.Menu>
    </>
  );
}

type ConditionalPermissionsWrapperProps = {
  permissions?: Permission[];
  children: React.JSX.Element;
};

const ConditionalPermissionsWrapper = ({
  permissions,
  children,
}: ConditionalPermissionsWrapperProps) =>
  permissions && permissions.length ? (
    <AllowedTo permissions={permissions}>{children}</AllowedTo>
  ) : (
    children
  );

export default ActionMenu;
